// infrastructure/api/coverageApi.js
import { auth } from 'boot/firebase';
import { serverTimestamp } from 'firebase/firestore';
import BillingIntentsDB from 'src/fb/billingIntents-db';
import ClientCoveragesDB from 'src/fb/clientCoverages-db';
import GenericDB from 'src/fb/generic-db';

import { BILLING_INTENT, INTENT } from '../../application/coverageChange';
import { isStamped } from '../../domain/model/EligibilityVerification';
import { toCoverage, toDocument } from '../mappers/coverageMapper';
import { toDocument as toBillingIntentDocument } from '../mappers/billingIntentMapper';

/**
 * Persistence for client coverages.
 *
 * `clientCoverages` is a flat root collection scoped by the document's `client` and `clinic`
 * fields — per the no-subcollections guideline, and the same shape as its nearest neighbor
 * `clientAuthorizations`. Those scoping keys are bare rather than `Id`-suffixed because the
 * Firestore rules helpers read those exact names; the mapper translates them for the domain.
 *
 * Removal is a SOFT delete via `isDeleted`, the platform pattern (see `clientAuthorizations`), so
 * a coverage that was removed can still be accounted for later. The rules grant no hard delete.
 *
 * Writes go through one transaction — see `applyChanges` for why that is a contract and not an
 * optimization.
 *
 * CONSEQUENCE FOR EVERY READER: this collection must always be queried with
 * `isDeleted == false`. A tombstone that slips past a reader is not inert — it would be selected
 * as the client's payer and billed.
 *
 * Tombstones carry `deleteTimestamp` and `deletedBy`, so a retention job can eventually purge them and
 * an audit can answer who removed a payer from a client. Nothing purges them today, and nothing
 * can from the client: the rules grant no `delete` at all, so a purge has to run server-side under
 * the admin SDK. That is the right shape — a retention sweep is not something a browser session
 * should be able to trigger.
 */

/**
 * New ids for documents that need one BEFORE they are written.
 *
 * Both collections stage locally — a coverage is arranged, edited and possibly removed before the
 * client is saved, and a billing block is decided against a coverage that may itself be staged —
 * so an id assigned by the server on write is too late. Firestore's own generator is used rather
 * than a UUID: the value is random either way, so writes spread across the key range identically,
 * but these read as native ids everywhere they surface.
 *
 * @returns {string}
 */
export function newCoverageId() {
  return new ClientCoveragesDB().newId();
} // newCoverageId()

/** @returns {string} */
export function newBillingIntentId() {
  return new BillingIntentsDB().newId();
} // newBillingIntentId()

/**
 * Loads a client's coverages.
 *
 * @param {{ clientId: string, clinicId: string }} scope
 * @returns {Promise<import('../../domain/model/Coverage.types').Coverage[]>}
 */
export async function loadClientCoverages({ clientId, clinicId }) {
  const documents = await new ClientCoveragesDB().readAll([
    ['clinic', '==', clinicId],
    ['client', '==', clientId],
    ['isDeleted', '==', false],
  ]);

  return documents.map(toCoverage);
} // loadClientCoverages()

/**
 * The OPEN billing intents for a client — one per paused coverage.
 *
 * `isBlocking == true` is the state filter — a single boolean, deliberately, so nothing is tempted
 * to resolve a timestamp pair against a service date. A released block is a closed record that
 * stays on file, not a deleted one; `isDeleted` is filtered separately and means what it means
 * everywhere else, entered in error.
 *
 * Returns `{ id, coverageId }` rather than bare coverage ids because releasing has to close a
 * SPECIFIC record, and a coverage may carry closed intents from earlier decisions.
 *
 * @param {{ clientId: string, clinicId: string }} scope
 * @returns {Promise<Array<{ id: string, coverageId: string }>>}
 */
export async function loadOpenBillingIntents({ clientId, clinicId }) {
  const documents = await new BillingIntentsDB().readAll([
    ['clinic', '==', clinicId],
    ['client', '==', clientId],
    ['isBlocking', '==', true],
    ['isDeleted', '==', false],
  ]);

  return documents.map((doc) => ({ id: doc.id, coverageId: doc.coverageId }));
} // loadOpenBillingIntents()

/**
 * Tombstone fields. Written explicitly rather than read off GenericDB's `updateTimestamp` /
 * `updateBy`, which every later write overwrites — a retention sweep deciding what to destroy, or
 * an audit asking who removed a payer, cannot rest on a value that moves.
 *
 * The names are the platform's soft-delete trio — `isDeleted` / `deleteTimestamp` / `deletedBy`,
 * as written by `sessions-db`, `sessionActivities-db`, `data-db` and the reporting dialogs. Worth
 * matching exactly rather than coining a synonym: a retention sweep is cross-collection by nature,
 * and one written against the established name would skip a tombstone called anything else — the
 * coverage would sit undestroyed forever, defeating the only reason the field is here.
 *
 * `deleteTimestamp` is a Firestore `Timestamp`, NOT a 'YYYY-MM-DD' string. A deliberate exception to how
 * this collection stores dates: effective bounds are calendar dates and must never be instants,
 * whereas a deletion is a moment. The clock is the server's, so a wrong device clock cannot move a
 * retention window.
 *
 * `auth` ITSELF IS OPTIONAL-CHAINED, not just `currentUser`, and every actor stamp in this file does
 * the same. `auth` is a mutable export: switching region tears the Firebase app down and
 * re-initializes it, so it is null for a window. Reaching through it unguarded throws a TypeError and
 * abandons the whole write — losing the deletion rather than merely losing the name of who made it.
 * An unattributed stamp is the right failure here; a refused write is not.
 */
const tombstone = () => ({
  isDeleted: true,
  deleteTimestamp: serverTimestamp(),
  deletedBy: auth?.currentUser?.uid ?? null,
});

/**
 * Applies a set of staged changes in a single transaction.
 *
 * Atomic because a reorder is one decision spread across several documents. Written as separate
 * calls, a partial failure leaves two coverages sharing a `cobRank` and the payer billed first
 * becomes a tie-break — and the client dialog closes even when a save fails, so there is no retry
 * to fall back on.
 *
 * `executeTransaction` also carries the offline path and the IndexedDB mirror, which hand-rolled
 * `writeBatch` calls here would lose. It must be called on a plain GenericDB, not a subclass.
 *
 * Billing intents ride the same transaction. They are a separate aggregate and could in principle
 * be written separately, but a coverage that saves without its pause is one the user chose not to
 * bill, silently billable — and the dialog closes on failure, so nobody would find out.
 *
 * @param {import('../../application/coverageChange').CoverageChange[]} changes
 * @param {import('../../application/coverageChange').BillingIntentChange[]} [intentChanges]
 * @returns {Promise<unknown>}
 */
export function applyChanges(changes, intentChanges = []) {
  if (changes.length === 0 && intentChanges.length === 0) {
    return Promise.resolve();
  }

  const coveragesDb = new ClientCoveragesDB();
  const billingIntentsDb = new BillingIntentsDB();

  /**
   * Completes a verification fact with the two things only this layer owns: the clock and the
   * current user. An UNSTAMPED fact is a new check — the domain records the verdict and leaves
   * these null, exactly as it does for `blockedBy`. A fact that already carries its stamp is
   * being carried through by an unrelated edit and must not be re-dated, or every save would
   * refresh the age of a check nobody repeated.
   */
  const withVerificationStamp = (document) => {
    if (isStamped(document.eligibilityVerification)) {
      return document;
    }
    return {
      ...document,
      eligibilityVerification: {
        ...document.eligibilityVerification,
        checkedAt: serverTimestamp(),
        checkedBy: auth?.currentUser?.uid ?? null,
      },
    };
  };

  return new GenericDB().executeTransaction(async (transaction) => {
    // No reads, so ordering against Firestore's reads-before-writes rule is not a concern.
    await Promise.all(changes.map(({ intent, coverage }) => {
      switch (intent) {
        case INTENT.CREATE:
          // The staged id becomes the document id, so a coverage keeps one identity from the
          // moment the user adds it through to the billing items derived from it.
          return transaction.create(coveragesDb, {
            data: withVerificationStamp(toDocument(coverage)),
            id: coverage.id,
          });
        case INTENT.UPDATE:
          return transaction.update(coveragesDb, {
            data: { ...withVerificationStamp(toDocument(coverage)), id: coverage.id },
          });
        case INTENT.DELETE:
          return transaction.update(coveragesDb, {
            data: { id: coverage.id, ...tombstone() },
          });
        default:
          // A change with no recognized intent is a programming error, and swallowing it is how a
          // save reports success having written nothing. Fail the transaction instead.
          throw new TypeError(`applyChanges: unknown coverage intent "${intent}"`);
      }
    }));

    await Promise.all(intentChanges.map(({ verb, billingIntent }) => {
      // Pausing writes a new record; releasing CLOSES the open one. Neither is a deletion: the
      // decision genuinely happened and applied for a period, so tombstoning it would assert it
      // never did and the next pause would overwrite the evidence.
      switch (verb) {
        case BILLING_INTENT.PAUSE:
          return transaction.create(billingIntentsDb, {
            data: {
              ...toBillingIntentDocument(billingIntent),
              blockedBy: auth?.currentUser?.uid ?? null,
            },
            id: billingIntent.id,
          });
        case BILLING_INTENT.RELEASE:
          return transaction.update(billingIntentsDb, {
            data: {
              id: billingIntent.id,
              isBlocking: false,
              releaseTimestamp: serverTimestamp(),
              releasedBy: auth?.currentUser?.uid ?? null,
            },
          });
        default:
          // Same reasoning, and it bites harder here: a silently skipped block leaves a coverage
          // the user chose not to bill looking billable, with the session reporting it saved.
          throw new TypeError(`applyChanges: unknown billing-intent verb "${verb}"`);
      }
    }));
  });
} // applyChanges()
